/* global google */
import type { LocationAutocompleteSuggestion } from '../../types/location/geocoding.type';
import {
  loadGoogleMapsApi,
  loadGoogleMapsPlacesLibrary,
} from './google-maps.loader';
import {
  mapGoogleGeocodedRecordToSuggestion,
  mapGooglePlaceRecordToSuggestion,
} from './google-maps-geocoding.util';

interface GoogleMapsPrediction {
  description: string;
  placeId: string;
  secondaryText?: string | null;
  placePrediction?: google.maps.places.PlacePrediction;
}

const DEFAULT_LANGUAGE = 'es';
const DEFAULT_LIMIT = 5;

let autocompleteSessionToken: google.maps.places.AutocompleteSessionToken | null =
  null;

const buildAutocompleteRequest = (params: {
  query: string;
  /** Explicit country restriction; omitted for worldwide search. */
  countryCode?: string | null;
  /** Regional ranking preference without excluding other countries. */
  regionCode?: string | null;
  sessionToken?: google.maps.places.AutocompleteSessionToken | null;
}): google.maps.places.AutocompleteRequest => {
  const countryCode = params.countryCode?.trim().toLowerCase();

  const regionCode = params.regionCode?.trim().toLowerCase();

  return {
    input: params.query,
    language: DEFAULT_LANGUAGE,
    region: countryCode?.length
      ? countryCode
      : regionCode?.length
        ? regionCode
        : undefined,
    includedRegionCodes: countryCode ? [countryCode] : undefined,
    sessionToken: params.sessionToken ?? undefined,
  };
};

const getAutocompleteSessionToken =
  async (): Promise<google.maps.places.AutocompleteSessionToken> => {
    if (autocompleteSessionToken) {
      return autocompleteSessionToken;
    }

    const { AutocompleteSessionToken } = await loadGoogleMapsPlacesLibrary();
    autocompleteSessionToken = new AutocompleteSessionToken();
    return autocompleteSessionToken;
  };

const getGeocoder = async (): Promise<google.maps.Geocoder> => {
  const maps = await loadGoogleMapsApi();
  return new maps.Geocoder();
};

const clearAutocompleteSessionToken = () => {
  autocompleteSessionToken = null;
};

/**
 * Returns Google Maps place predictions for the provided address query.
 */
export const searchGoogleMapsLocationPredictions = async (params: {
  query: string;
  /** Explicit country restriction; omitted for worldwide search. */
  countryCode?: string | null;
  /** Regional ranking preference without excluding other countries. */
  regionCode?: string | null;
  limit?: number;
}): Promise<GoogleMapsPrediction[]> => {
  const query = params.query.trim();
  if (query.length < 3) {
    clearAutocompleteSessionToken();
    return [];
  }

  const { AutocompleteSuggestion } = await loadGoogleMapsPlacesLibrary();
  const sessionToken = await getAutocompleteSessionToken();

  const { suggestions = [] } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(
      buildAutocompleteRequest({
        query,
        countryCode: params.countryCode,
        regionCode: params.regionCode,
        sessionToken,
      })
    );

  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter(
      (prediction): prediction is google.maps.places.PlacePrediction =>
        prediction != null
    )
    .slice(0, params.limit ?? DEFAULT_LIMIT)
    .map((prediction) => ({
      description: prediction.text.text,
      placeId: prediction.placeId,
      secondaryText: prediction.secondaryText?.text ?? null,
      placePrediction: prediction,
    }))
    .filter((prediction) => prediction.description.trim().length > 0);
};

/**
 * Resolves a Google Maps prediction into the shared normalized suggestion shape.
 */
export const resolveGoogleMapsLocationPrediction = async (
  prediction: GoogleMapsPrediction
): Promise<LocationAutocompleteSuggestion | null> => {
  if (prediction.placePrediction) {
    const place = prediction.placePrediction.toPlace();
    await place.fetchFields({
      fields: [
        'id',
        'formattedAddress',
        'displayName',
        'location',
        'addressComponents',
      ],
    });

    clearAutocompleteSessionToken();
    return mapGooglePlaceRecordToSuggestion(place);
  }

  const geocoder = await getGeocoder();

  const results = await new Promise<google.maps.GeocoderResult[]>(
    (resolve, reject) => {
      void geocoder.geocode(
        { placeId: prediction.placeId },
        (nextResults, status) => {
          if (
            status === google.maps.GeocoderStatus.ZERO_RESULTS ||
            !nextResults
          ) {
            resolve([]);
            return;
          }

          if (status !== google.maps.GeocoderStatus.OK) {
            reject(new Error('Unable to resolve Google Maps place details.'));
            return;
          }

          resolve(nextResults);
        }
      );
    }
  );

  clearAutocompleteSessionToken();
  return mapGoogleGeocodedRecordToSuggestion(results[0] ?? {});
};

/**
 * Reverse geocodes coordinates through Google Maps.
 */
export const reverseGeocodeGoogleMapsLocation = async (params: {
  latitude: number;
  longitude: number;
}): Promise<LocationAutocompleteSuggestion | null> => {
  const geocoder = await getGeocoder();

  const results = await new Promise<google.maps.GeocoderResult[]>(
    (resolve, reject) => {
      void geocoder.geocode(
        {
          location: {
            lat: params.latitude,
            lng: params.longitude,
          },
          language: DEFAULT_LANGUAGE,
        },
        (nextResults, status) => {
          if (
            status === google.maps.GeocoderStatus.ZERO_RESULTS ||
            !nextResults
          ) {
            resolve([]);
            return;
          }

          if (status !== google.maps.GeocoderStatus.OK) {
            reject(
              new Error('Unable to reverse geocode Google Maps coordinates.')
            );
            return;
          }

          resolve(nextResults);
        }
      );
    }
  );

  return mapGoogleGeocodedRecordToSuggestion(results[0] ?? {});
};

export type { GoogleMapsPrediction };
